/* eslint-disable prefer-destructuring */
import React, { useState, useEffect } from 'react';
import { Button, Modal } from 'react-bootstrap';
import classNames from 'classnames';
import utils from '../../../utils/utils';
import { useAddAgencyContactMutation, useUpdateAgencyContactMutation } from '../../../redux/external-agency';
import ConfirmationModal from '../../../components/modals/ConfirmationModalNew';

import './AddContactModal.scss';

const AddContactModal = ({
	userData = {},
	contact = {},
	onCancel,
	onSaveSuccess,
	showNotification,
	hasContactsChangeStatusPermission
}) => {
	const [addAgencyContact] = useAddAgencyContactMutation();
	const [updateAgencyContact] = useUpdateAgencyContactMutation();

	const [firstName, setFirstName] = useState('');
	const [lastName, setLastName] = useState('');
	const [email, setEmail] = useState('');
	const [phoneNumber, setPhoneNumber] = useState('');
	const [phoneExtensionNumber, setPhoneExtensionNumber] = useState('');
	const [isPortalEnabled, setIsPortalEnabled] = useState(true);
	const [notifyClaimsEnabled, setNotifyClaimsEnabled] = useState(false);
	const [notifyShiftCancelEnabled, setNotifyShiftCancelEnabled] = useState(false);
	const [notifyMissedPunchSummaryEnabled, setNotifyMissedPunchSummaryEnabled] = useState(false);
	const [notifyOpenShiftsSummaryEnabled, setNotifyOpenShiftsSummaryEnabled] = useState(false);
	const [receivePaymentAdvice, setReceivePaymentAdvice] = useState(false);
	const [notifyHcpBundleRequestEnabled, setNotifyHcpBundleRequestEnabled] = useState(false);
	const [errors, setErrors] = useState(null);
	const [showStatusToggleConfirmationModal, setShowStatusToggleConfirmationModal] = useState(false);
	const [showAdminToggleConfirmationModal, setShowAdminToggleConfirmationModal] = useState(false);
	const [isAdmin, setIsAdmin] = useState(false);
	const isEditMode = !!contact.id;
	const canChangeContactStatus = hasContactsChangeStatusPermission && userData.agencyContactId !== contact.id;

	useEffect(() => {
		if (contact.id) {
			setFirstName(contact.first_name);
			setLastName(contact.last_name);
			setEmail(contact.email);
			setPhoneNumber(utils.formatPhoneNumber(contact.phone_number || ''));
			setPhoneExtensionNumber(contact.phone_extension_number || '');
			setIsPortalEnabled(!!contact.is_portal_enabled);
			setNotifyClaimsEnabled(!!contact.notify_claims_enabled);
			setNotifyShiftCancelEnabled(!!contact.notify_shift_cancel_enabled);
			setNotifyMissedPunchSummaryEnabled(!!contact.notify_missed_punch_summary_enabled);
			setNotifyOpenShiftsSummaryEnabled(!!contact.notify_open_shifts_summary_enabled);
			setReceivePaymentAdvice(!!contact.receive_payment_advice);
			setNotifyHcpBundleRequestEnabled(!!contact.notify_hcp_bundle_request_enabled);
			setIsAdmin(!!contact.is_admin);
		}
	}, [contact]);

	const updateFirstName = (e) => {
		if (!e.target.value || utils.nameRegex.test(e.target.value)) {
			setFirstName(e.target.value);
		}
	};
	const updateLastName = (e) => {
		if (!e.target.value || utils.nameRegex.test(e.target.value)) {
			setLastName(e.target.value);
		}
	};
	const updatePhoneExtensionNumber = (e) => {
		if (!e.target.value || utils.alphanumericRegex.test(e.target.value)) {
			setPhoneExtensionNumber(e.target.value.substring(0, 5));
		}
	};
	const updatePhoneNumber = (e) => {
		let value = e.target.value;
		if (utils.invalidPhoneCharactersRegex.test(value)) {
			value = value.replace(utils.invalidPhoneCharactersRegex, '');
		}
		if (value.length > 12) {
			e.preventDefault();
		} else {
			setPhoneNumber(utils.formatPhoneNumber(value));
		}
	};

	const validateInputs = () => {
		const _errors = {};
		if (!firstName) {
			_errors.firstName = 'First name is required';
		}
		if (!lastName) {
			_errors.lastName = 'Last name is required';
		}
		if (!email) {
			_errors.email = 'Email is required';
		} else if (!utils.emailRegex.test(email.trim())) {
			_errors.email = 'Email format is invalid';
		}
		if (phoneNumber && !utils.phoneNumberRegex.test(phoneNumber)) {
			_errors.phoneNumber = 'Phone number format is invalid';
		}
		return _errors;
	};

	const onSave = async () => {
		const validationErrors = validateInputs();
		setErrors(validationErrors);

		if (!Object.keys(validationErrors).length) {
			const basePayload = {
				first_name: firstName.trim(),
				last_name: lastName.trim(),
				phone: phoneNumber.replaceAll('-', ''),
				phone_extension_number: phoneExtensionNumber ? phoneExtensionNumber.trim() : null
			};

			if (isEditMode) {
				if (canChangeContactStatus) {
					basePayload.is_portal_enabled = isPortalEnabled;
					basePayload.is_admin = isAdmin;
				}

				basePayload.notify_claims_enabled = notifyClaimsEnabled;
				basePayload.notify_shift_cancel_enabled = notifyShiftCancelEnabled;
				basePayload.notify_missed_punch_summary_enabled = notifyMissedPunchSummaryEnabled;
				basePayload.notify_open_shifts_summary_enabled = notifyOpenShiftsSummaryEnabled;
				basePayload.receive_payment_advice = receivePaymentAdvice;
				basePayload.notify_hcp_bundle_request_enabled = notifyHcpBundleRequestEnabled;

				try {
					await updateAgencyContact({ id: contact.id, body: basePayload }).unwrap();
					showNotification('info', 'Contact saved successfully');
					onSaveSuccess();
				} catch (e) {
					showNotification('error', e.data?.errors?.[0]?.message || 'Failed to save contact. Please try again.');
				}
			} else {
				try {
					await addAgencyContact({ ...basePayload, email: email.trim() }).unwrap();
					showNotification('info', 'Contact added successfully');
					onSaveSuccess();
				} catch (e) {
					showNotification('error', e.data?.errors?.[0]?.message || 'Failed to add contact. Please try again.');
				}
			}
		}
	};

	const showStatusChangeConfirmation = () => setShowStatusToggleConfirmationModal(true);
	const toggleStatusOnConfirm = () => {
		setIsPortalEnabled(!isPortalEnabled);
		setShowStatusToggleConfirmationModal(false);
	};
	const showAdminChangeConfirmation = () => setShowAdminToggleConfirmationModal(true);
	const toggleAdminOnConfirm = () => {
		setIsAdmin(!isAdmin);
		setShowAdminToggleConfirmationModal(false);
	};
	return [
		<Modal
			key="add-contact-modal"
			size="lg"
			show
			onHide={onCancel}
			centered
			aria-labelledby="contained-modal-title-vcenter"
			className={classNames('add-contact-modal', {
				backdrop: showStatusToggleConfirmationModal || showAdminToggleConfirmationModal
			})}
		>
			<Modal.Header closeButton>
				<Modal.Title id="ontained-modal-title-vcenter">{isEditMode ? 'Edit' : 'Add'} contact</Modal.Title>
			</Modal.Header>
			<Modal.Body>
				<div className="row">
					<div className={classNames('input-group', { invalid: errors?.firstName })}>
						<label htmlFor="firstname">First name</label>
						<div className="input-wrapper">
							<input
								id="firstname"
								type="text"
								value={firstName}
								onChange={updateFirstName}
								maxLength={50}
								autoComplete="off"
							/>
						</div>
						<div className="invalid-field-message">{errors?.firstName}</div>
					</div>
					<div className={classNames('input-group', { invalid: errors?.lastName })}>
						<label htmlFor="lastname">Last name</label>
						<div className="input-wrapper">
							<input
								id="lastname"
								type="text"
								value={lastName}
								onChange={updateLastName}
								maxLength={50}
								autoComplete="off"
							/>
						</div>
						<div className="invalid-field-message">{errors?.lastName}</div>
					</div>
				</div>

				<div className="row">
					<div
						className={classNames('input-group', {
							invalid: errors?.email,
							disabled: isEditMode
						})}
					>
						<label htmlFor="email">Email</label>
						<div className="input-wrapper">
							<input
								id="email"
								type="email"
								value={email}
								maxLength={129}
								onChange={(e) => setEmail(e.target.value)}
								autoComplete="off"
								disabled={isEditMode}
							/>
						</div>
						<div className="invalid-field-message">{errors?.email}</div>
					</div>
				</div>
				<div className="row">
					<div className={classNames('input-group', { invalid: errors?.phoneNumber })}>
						<label htmlFor="phone">
							<span>Phone</span>
							<span>(Optional)</span>
						</label>
						<div className="input-wrapper">
							<input
								id="phone"
								type="text"
								value={phoneNumber}
								onChange={updatePhoneNumber}
								maxLength={129}
								autoComplete="off"
							/>
						</div>
						<div className="invalid-field-message">{errors?.phoneNumber}</div>
					</div>
					<div className="input-group">
						<label htmlFor="ext">
							<span>Ext</span>
							<span>(If applicable)</span>
						</label>
						<input
							id="ext"
							type="text"
							pattern={utils.alphanumericRegex}
							value={phoneExtensionNumber}
							onChange={updatePhoneExtensionNumber}
							autoComplete="off"
							maxLength="5"
						/>
					</div>
				</div>
				{isEditMode && canChangeContactStatus && (
					<div className="row">
						<div className="input-group">
							<div className="label">Statuses</div>
							<div className="checkbox-field-wrapper">
								<input
									type="checkbox"
									id="status-checkbox"
									onChange={showStatusChangeConfirmation}
									value={isPortalEnabled}
									checked={isPortalEnabled}
								/>
								<label htmlFor="status-checkbox">Enable portal access</label>
							</div>
						</div>
						<div className="input-group">
							<div className="label">&nbsp;</div>
							<div className="checkbox-field-wrapper">
								<input
									type="checkbox"
									id="admin-checkbox"
									onChange={showAdminChangeConfirmation}
									value={isAdmin}
									checked={isAdmin}
								/>
								<label htmlFor="admin-checkbox">Admin user</label>
							</div>
						</div>
					</div>
				)}
				{isEditMode && (
					<div className="row">
						<div className="input-group">
							<div className="label">Notifications</div>
							<div className="checkbox-field-wrapper">
								<input
									type="checkbox"
									id="notification-claims-checkbox"
									onChange={() => setNotifyClaimsEnabled(!notifyClaimsEnabled)}
									value={notifyClaimsEnabled}
									checked={notifyClaimsEnabled}
								/>
								<label htmlFor="notification-claims-checkbox">Shifts claimed by professionals</label>
							</div>
							<div className="checkbox-field-wrapper">
								<input
									type="checkbox"
									id="notification-shift-missed-punch-checkbox"
									onChange={() => setNotifyMissedPunchSummaryEnabled(!notifyMissedPunchSummaryEnabled)}
									value={notifyMissedPunchSummaryEnabled}
									checked={notifyMissedPunchSummaryEnabled}
								/>
								<label htmlFor="notification-shift-missed-punch-checkbox">Missed punch summary</label>
							</div>
							<div className="checkbox-field-wrapper">
								<input
									type="checkbox"
									id="notification-shift-receive-payment-advice"
									onChange={() => setReceivePaymentAdvice(!receivePaymentAdvice)}
									value={receivePaymentAdvice}
									checked={receivePaymentAdvice}
								/>
								<label htmlFor="notification-shift-receive-payment-advice">Receive payment advice</label>
							</div>
						</div>
						<div className="input-group">
							<div className="label">&nbsp;</div>
							<div className="checkbox-field-wrapper">
								<input
									type="checkbox"
									id="notification-shift-cancel-checkbox"
									onChange={() => setNotifyShiftCancelEnabled(!notifyShiftCancelEnabled)}
									value={notifyShiftCancelEnabled}
									checked={notifyShiftCancelEnabled}
								/>
								<label htmlFor="notification-shift-cancel-checkbox">Shifts cancelled by facility</label>
							</div>
							<div className="checkbox-field-wrapper">
								<input
									type="checkbox"
									id="notification-open-shifts-checkbox"
									onChange={() => setNotifyOpenShiftsSummaryEnabled(!notifyOpenShiftsSummaryEnabled)}
									value={notifyOpenShiftsSummaryEnabled}
									checked={notifyOpenShiftsSummaryEnabled}
								/>
								<label htmlFor="notification-open-shifts-checkbox">Open shift summary</label>
							</div>
							<div className="checkbox-field-wrapper">
								<input
									type="checkbox"
									id="notification-hcp-bundle-request-checkbox"
									onChange={() => setNotifyHcpBundleRequestEnabled(!notifyHcpBundleRequestEnabled)}
									value={notifyHcpBundleRequestEnabled}
									checked={notifyHcpBundleRequestEnabled}
								/>
								<label htmlFor="notification-hcp-bundle-request-checkbox">Receive HCP bundle request</label>
							</div>
						</div>
					</div>
				)}
			</Modal.Body>
			<Modal.Footer>
				<Button onClick={onCancel} variant="tertiary">
					Cancel
				</Button>
				<Button variant="primary" disabled={false} className="g-0" onClick={onSave}>
					{isEditMode ? 'Save changes' : 'Add contact'}
				</Button>
			</Modal.Footer>
		</Modal>,

		showAdminToggleConfirmationModal && (
			<ConfirmationModal
				onConfirm={toggleAdminOnConfirm}
				onCancel={() => setShowAdminToggleConfirmationModal(false)}
				confirmLabel={isAdmin ? 'Remove admin' : 'Confirm admin'}
				cancelLabel="Cancel"
				key="admin-toggle-confirmation"
				title={isAdmin ? 'Remove admin permission?' : 'Grant admin access?'}
			>
				{isAdmin
					? "This action will remove this user's ability to manage other portal users' profile and permissions."
					: "As an admin, this user will be able to manage other agency portal users' profile, permissions, portal access, and admin status."}
			</ConfirmationModal>
		),

		showStatusToggleConfirmationModal && (
			<ConfirmationModal
				onConfirm={toggleStatusOnConfirm}
				onCancel={() => setShowStatusToggleConfirmationModal(false)}
				confirmLabel={isPortalEnabled ? 'Disable contact' : 'Re-enable contact'}
				cancelLabel={isPortalEnabled ? 'Keep enabled' : 'Keep disabled'}
				key="status-toggle-confirmation"
				title={isPortalEnabled ? 'Disable contact?' : 'Re-enable contact?'}
			>
				{isPortalEnabled ? (
					<span>
						This contact is currently enabled; if you disable them, they will <b>no longer</b> be able to access the
						portal.
					</span>
				) : (
					'This contact is currently disabled; if you re-enabled them, they will be able to access the portal.'
				)}
			</ConfirmationModal>
		)
	];
};

export default AddContactModal;
